import { User, AuthService } from './interfaces';
import { auth } from './firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification as sendFirebaseEmailVerification,
  onAuthStateChanged,
  deleteUser,
  sendPasswordResetEmail,
} from "firebase/auth";
import { User as FirebaseUser } from 'firebase/auth'
import {
  EmailAlreadyInUseError,
  IncorrectCredentialsError,
  InvalidEmailError,
  UnkownLoginError,
  UnkownSignupError,
  UserNotFoundError,
  WeakPasswordError,
  TooManyRequestsError,
} from './errors/AuthErrors';
import { actionCodeSettings } from '../config/configs';
import LocalStorageService from "./utils/LocalStorage";
import { logger } from './LoggerService';

/**
 * Maps a Firebase user object to our User schema
 * Only extracts the fields defined in the User interface
 */
const mapFirebaseUserToUser = (firebaseUser: FirebaseUser): User => {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    emailVerified: firebaseUser.emailVerified,
    metadata: {
      createdAt: firebaseUser.metadata.creationTime,
      creationTime: firebaseUser.metadata.creationTime,
      lastLoginAt: firebaseUser.metadata.lastSignInTime,
      lastSignInTime: firebaseUser.metadata.lastSignInTime,
    }
  };
};

// Implement authentication service using Firebase
export class FirebaseAuthService implements AuthService {

  constructor() {
    onAuthStateChanged(auth, (firebaseUser: any) => {
      if (firebaseUser) {
        const user = mapFirebaseUserToUser(firebaseUser);
        LocalStorageService.setItem('auth', user);
      } else {
        LocalStorageService.removeItem('auth');
      }
    });
  }

  signup = async (email: string, password: string): Promise<User> => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await this.sendEmailVerification();
      return mapFirebaseUserToUser(user);
    } catch (error: any) {
      logger.error('Signup failed', error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          throw new EmailAlreadyInUseError();
        case 'auth/invalid-email':
          throw new InvalidEmailError();
        case 'auth/operation-not-allowed':
          throw new UnkownSignupError();
        case 'auth/weak-password':
          throw new WeakPasswordError();
        case 'auth/too-many-requests':
          throw new TooManyRequestsError();
        default:
          throw new UnkownSignupError();
      }
    }
  }

  login = async (email: string, password: string): Promise<User> => {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      return mapFirebaseUserToUser(user);
    } catch (error: any) {
      logger.error('Login failed', error);
      switch (error.code) {
        case 'auth/user-not-found':
          throw new UserNotFoundError();
        case 'auth/user-disabled':
          throw new UserNotFoundError();
        case 'auth/wrong-password':
          throw new IncorrectCredentialsError();
        case 'auth/invalid-email':
          throw new IncorrectCredentialsError();
        case 'auth/invalid-login-credentials':
          throw new IncorrectCredentialsError();
        case 'auth/too-many-requests':
          throw new TooManyRequestsError();
        default:
          throw new UnkownLoginError();
      }
    }
  }

  logout = async (): Promise<void> => {
    await auth.signOut();
  }

  deleteUser = async (): Promise<void> => {
    if (auth.currentUser) {
      await deleteUser(auth.currentUser);
    }
  }

  sendEmailVerification = async (): Promise<void> => {
    try {
      await sendFirebaseEmailVerification(auth.currentUser as FirebaseUser, actionCodeSettings);
    } catch (error: any) {
      logger.error('Error sending verification email', error);
      switch (error.code) {
        case 'auth/too-many-requests':
          throw new TooManyRequestsError();
        case 'auth/invalid-continue-uri':
          logger.error('Invalid continue URI configuration', error);
          throw new UnkownSignupError();
        default:
          throw new UnkownSignupError();
      }
    }
  }

  sendPasswordResetEmail = async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      logger.error('Error sending password reset email', error);
      switch (error.code) {
        case 'auth/user-not-found':
          throw new UserNotFoundError();
        case 'auth/invalid-email':
          throw new InvalidEmailError();
        case 'auth/too-many-requests':
          throw new TooManyRequestsError();
        default:
          throw new UnkownLoginError();
      }
    }
  }

  getCurrentUser = (): User | null => {
    return LocalStorageService.getItem<User>('auth');
  }
}

const fb = new FirebaseAuthService();
export default fb;
